import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, TrendingUp, Zap, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const PlanUsageBanner = ({ onUpgradeClick }) => {
  const { token, user, getAuthHeaders } = useAuth();
  // Note: Using token directly for specific auth checks
  const [planUsage, setPlanUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchPlanUsage();
  }, [token]);

  const fetchPlanUsage = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me/plan-usage`, {
        headers: getAuthHeaders()
      });
      setPlanUsage(response.data.data);
    } catch (err) {
      console.error('Error fetching plan usage:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoUpgrade = async () => {
    if (!planUsage) return;
    
    try {
      const newValue = !planUsage.auto_upgrade_enabled;
      await axios.put(
        `${API_BASE_URL}/auth/me/auto-upgrade?enabled=${newValue}`,
        {},
        { headers: getAuthHeaders() }
      );
      setPlanUsage(prev => ({ ...prev, auto_upgrade_enabled: newValue }));
    } catch (err) {
      console.error('Error toggling auto-upgrade:', err);
    }
  };

  if (loading || !planUsage || !planUsage.has_limits || dismissed) {
    return null;
  }

  const { appointments, plan_type, auto_upgrade_enabled, features } = planUsage;
  const warningLevel = appointments?.warning_level;

  // Only show banner if approaching or at limit
  if (!warningLevel) {
    return null;
  }

  const isLimitReached = warningLevel === 'limit_reached';
  const isApproachingLimit = warningLevel === 'approaching_limit';

  return (
    <div className={`mb-6 rounded-lg border-l-4 p-4 ${
      isLimitReached 
        ? 'bg-red-50 border-red-500' 
        : 'bg-yellow-50 border-yellow-500'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start">
          <div className={`flex-shrink-0 ${isLimitReached ? 'text-red-500' : 'text-yellow-500'}`}>
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="ml-3">
            <h3 className={`text-sm font-semibold ${isLimitReached ? 'text-red-800' : 'text-yellow-800'}`}>
              {isLimitReached 
                ? '⚠️ Appointment Limit Reached!' 
                : '📊 Approaching Appointment Limit'}
            </h3>
            <div className={`mt-1 text-sm ${isLimitReached ? 'text-red-700' : 'text-yellow-700'}`}>
              <p>
                You have used <strong>{appointments.used}</strong> of <strong>{appointments.limit}</strong> appointments 
                this month ({appointments.usage_percentage}%).
                {appointments.remaining !== 'unlimited' && appointments.remaining > 0 && (
                  <span> Only <strong>{appointments.remaining}</strong> remaining.</span>
                )}
              </p>
              {isLimitReached && !auto_upgrade_enabled && (
                <p className="mt-1 font-medium">
                  New appointments are blocked until you upgrade or enable auto-upgrade.
                </p>
              )}
            </div>
            
            {/* Progress bar */}
            <div className="mt-3 w-full bg-gray-200 rounded-full h-2.5 max-w-md">
              <div 
                className={`h-2.5 rounded-full ${isLimitReached ? 'bg-red-500' : 'bg-yellow-500'}`}
                style={{ width: `${Math.min(100, appointments.usage_percentage)}%` }}
              ></div>
            </div>

            {/* Actions */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {/* Auto-upgrade toggle */}
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={auto_upgrade_enabled}
                  onChange={handleToggleAutoUpgrade}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Auto-upgrade when limit reached
                </span>
              </label>

              {/* Upgrade button */}
              <button
                onClick={onUpgradeClick}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                  isLimitReached
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                }`}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Upgrade Plan
              </button>
            </div>

            {auto_upgrade_enabled && (
              <p className="mt-2 text-xs text-green-700 flex items-center">
                <Zap className="w-3 h-3 mr-1" />
                Auto-upgrade is enabled. Your plan will automatically upgrade when you reach the limit.
              </p>
            )}
          </div>
        </div>
        
        {/* Dismiss button */}
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default PlanUsageBanner;
